import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import { ManagementContractContext, updateManagementContractContext } from "@eres/ejari-core/contexts/ManagementContractContext";
import Strings from "@my-dld/assets/strings";
import FormSection from "@my-dld/components/Form/FormSection";
import FormAction from "@my-dld/components/Form/FormAction";
import FormRow from "@eres/forms-web/FormRow";
import { ImperialSystem } from "@my-dld/utils/UnitUtils";
import { PropertyTable } from "./Properties";
import { EjariProperty, EjariPropertyType } from "@eres/ejari-api/Entity";
import { EjariApi } from "@eres/ejari-api";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { getEjariActiveUserId, getEjariParticipantId, getEjariUserType, isOwnerLoggedIn, getParticipantID } from "@10x/sso";
import { getLeaseSearchQuery, getPropertiesRowSearchQuery } from "@eres/ejari-api/api";
import { ProjectProperties, ValidateMGMContractRequest, ValidateMGMProperty } from "@eres/ejari-api/Entity/management_contract";
import { ContractValidationError } from "@eres/ejari-api/Entity/tenant";
import _ from "lodash";

interface Props extends RoutedViewProps {
}

interface State {
	properties?: EjariProperty[];
	selectedProperties?: EjariProperty[];
	errorProperties?: ContractValidationError[];
}

class PropertySelection extends Component<Props, State> {
	static contextType = ManagementContractContext;
	context!: React.ContextType<typeof ManagementContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {};
	}

	componentDidMount(): void {
		this.setState( { properties: [ ...this.context.properties! ], selectedProperties: [ ...this.context.properties! ] } );
		this.loadProperties();
	}

	render() {
		const { properties, errorProperties, selectedProperties } = this.state;
		return <div className="tender-registration-form">
			<FormSection
				title={ `${ Strings.total_properties } (${ properties?.length ?? 0 })` }
			>
				<p> { `${ Strings.selected_properties } (${ selectedProperties?.length ?? 0 })` }</p>
				{ properties && properties.length > 0 ? <PropertyTable
					type={ properties?.[ 0 ].PropertyTypeId }
					onClick={ this.onAddRemove }
					properties={ properties.map( p => {
						const hasError = errorProperties && errorProperties?.find( e => e.CodeCategory + "" === p.TabuPropertyId + "" );
						return {
							...p,
							isSelected: this.isSelected( p ),
							hasError: hasError,
						};
					} ) }
					unit={ ImperialSystem }/> : null }
			</FormSection>
			<FormRow>
				<FormAction
					primaryActionLabel={ Strings.update }
					onPrimaryAction={ this.onContinue }
					secondaryActionLabel={ Strings.back }
					onSecondaryAction={ this.onBack }/>
			</FormRow>
		</div>;
	}

	private isSelected( p: EjariProperty ) {
		const index = this.state.selectedProperties?.findIndex( property => property.TabuPropertyId === p.TabuPropertyId );
		return index != null && index !== -1;
	}

	private loadProperties = () => {
		const ogProperty = this.context.ogProperty;
		const api = ogProperty?.PropertyType === EjariPropertyType.VILLA ? EjariApi.getProjectVillaList : EjariApi.getBuildingUnits;
		let searchQuery = "";
		if ( ogProperty?.PropertyType === EjariPropertyType.UNIT ) {
			searchQuery = getPropertiesRowSearchQuery( 0, 0, ogProperty?.BuildingPropertyIdKey );
		} else if ( ogProperty?.PropertyType === EjariPropertyType.VILLA ) {
			searchQuery = getPropertiesRowSearchQuery( 0, 0, ogProperty?.LandPropertyIdKey );
		}
		showLoading();
		api( searchQuery ).then( response => {
			hideLoading();
			const properties: EjariProperty[] = [];
			let selectedIndex = 0;
			response.Response.forEach( ( p: any, index: number ) => {
				const ejProp = this.convertProjectPropertyToEJProperty( p );
				if ( ejProp.TabuPropertyId === ogProperty?.TabuPropertyId ) {
					selectedIndex = index;
				}
				properties.push( ejProp );
			} );
			properties.splice( selectedIndex, 1 );
			this.setState( { ...this.state, properties: [ ogProperty!, ...properties ] } );
		} )
		.catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	};

	private onAddRemove = ( property: EjariProperty ) => {
		const ogProperty = this.context.ogProperty;
		if ( property.TabuPropertyId === ogProperty?.TabuPropertyId ) return;
		const properties = [ ...this.state.selectedProperties! ];
		const errorProperties = _.clone( this.state.errorProperties );
		const index = this.state.selectedProperties?.findIndex( p => p.TabuPropertyId === property.TabuPropertyId );
		if ( index != null && index !== -1 ) {
			const errorIndex = this.state.errorProperties?.findIndex( p => p.CodeCategory === property.TabuPropertyId + "" );
			if ( errorIndex != null && errorIndex !== -1 ) {
				errorProperties?.splice( errorIndex, 1 );
			}
			properties.splice( index, 1 );
		} else {
			properties.push( property );
		}
		this.setState( { ...this.state, selectedProperties: properties, errorProperties: errorProperties } );
	};

	private onValidate = (): Promise<boolean> => {
		showLoading();
		let properties: ValidateMGMProperty[] = [];
		this.state.selectedProperties?.forEach( ( value ) => {
			const m: ValidateMGMProperty = {
				EjariPropertyID: value.EjariPropertyId,
				PropertyId: value.TabuPropertyId,
				HoldType: Number( value.IsFreeHold ),
			};
			properties.push( m );
		} );
		const request: ValidateMGMContractRequest = {
			ActiveUserID: getEjariActiveUserId(),
			ActiveUserType: getEjariUserType(),
			ManagementCompanyId: 0,
			OwnerParticipantId: isOwnerLoggedIn() ? getParticipantID() : 0,
			Properties: properties,
		};
		return EjariApi.validatePropertiesForMGMContract( request )
		.then( resp => {
			hideLoading();
			const props: any[] = resp.PropertiesList;
			const result: EjariProperty[] = this.state.selectedProperties as EjariProperty[];
			const errors: ContractValidationError[] = resp.ValidationErrors;
			if ( errors && Array.isArray( errors ) && errors.length > 0 ) {
				showErrorMessage( Strings.an_error_occurred );
				this.setState( { ...this.state, errorProperties: errors } );
				return false;
			}
			props.forEach( ( val ) => {
				const index = result.findIndex( e => e.TabuPropertyId === val.PropertyId );
				if ( index && index > -1 ) {
					result[ index ].EjariPropertyId = val.EjariPropertyId;
				}
			} );
			this.setState( { selectedProperties: result } );
			return true;
		} )
		.catch( () => {
			hideLoading();
			return false;
		} );
	};

	private onContinue = async () => {
		const valid = await this.onValidate();
		if ( valid ) {
			updateManagementContractContext( { ...this.context, properties: this.state.selectedProperties } );
			this.props.history.goBack();
		}
	};

	private onBack = () => {
		this.props.history.goBack();
	};

	private getAllUnmanagedBuildingUnitsSearchQuery( buildingPropertyId?: number ) {
		let query = getLeaseSearchQuery();
		query += "&buildingPropertyId=" + buildingPropertyId;
		query += "&rentalStatus=0";
		query += "&isManaged=0";
		query += "&startIndex=0";
		query += "&maxRows=0";
		return query;
	}

	private getAllUnmanagedLandVillasSearchQuery( landPropertyId?: number ) {
		let query = "";
		const userTypeId = getEjariUserType();
		query += "&participantid=" + getEjariParticipantId();
		query += "&userTypeId=" + userTypeId;
		query += "&landPropertyId=" + landPropertyId;
		query += "&isManaged=0";
		query += "&managementCompanyId=0";
		return query;
	}

	private convertProjectPropertyToEJProperty( oProperty: ProjectProperties ): EjariProperty {
		const ejProp: Partial<EjariProperty> = {
			EjariPropertyId: oProperty.VillaEjariPropId,
			TabuPropertyId: oProperty.PropertyId,
			PropertyTypeId: oProperty.PropertyTypeId,
			UnitNo: oProperty.PropertyNumber,
			PropertyNumber: oProperty.PropertyNumber,
			AreaEn: oProperty.LandAreaNameEn,
			AreaAr: oProperty.LandAreaNameAr,
			BuildingNameEn: oProperty.BuildingNameEn,
			BuildingNameAr: oProperty.BuildingNameAr,
			IsFreeHold: oProperty.IsFreeHold,
		};
		return ejProp as EjariProperty;
	}
}

export default PropertySelection;
